/**
 * Agent Store
 *
 * Zustand store for managing the AI agent session state.
 * Simulates the curriculum import workflow.
 */

import { create } from 'zustand';
import { generateId } from '../lib/utils/idGenerator';
import {
  AgentSimulator,
  type ExtractedEntities,
} from '../lib/agentSimulator';
import type {
  CpAgentSession,
  CpAgentMessage,
  AgentConfig,
  AgentSessionStatus,
  ClarificationQuestion,
  ValidationError,
} from '../types/curriculum_planning';
import { defaultAgentConfig } from '../lib/mockData/curriculumPlanning/agent';
import { useCurriculumPlanningStore } from './curriculumPlanningStore';

// ============================================================================
// State Interface
// ============================================================================

interface AgentState {
  // Session State
  currentSessionId: string | null;
  sessions: CpAgentSession[];
  messages: CpAgentMessage[];

  // Processing State
  isProcessing: boolean;
  uploadedFiles: Array<{ id: string; name: string; size: number }>;

  // Extracted Data
  extractedEntities: ExtractedEntities | null;
  extractedCounts: Record<string, number> | null;

  // Clarifications & Validation
  pendingClarifications: ClarificationQuestion[];
  clarificationResponses: Record<string, string>;
  validationErrors: ValidationError[];

  // Report
  report: string | null;

  // ============================================================================
  // Getters
  // ============================================================================

  getCurrentSession: () => CpAgentSession | null;
  getSessionMessages: () => CpAgentMessage[];
  getSessionStatus: () => AgentSessionStatus | null;
  hasPendingClarifications: () => boolean;
  hasValidationErrors: () => boolean;

  // ============================================================================
  // Session Actions
  // ============================================================================

  createSession: (config?: Partial<AgentConfig>) => string;
  clearSession: () => void;
  cancelSession: () => void;

  // ============================================================================
  // File Actions
  // ============================================================================

  uploadFiles: (files: File[]) => Promise<void>;
  clearUploadedFiles: () => void;

  // ============================================================================
  // Processing Actions
  // ============================================================================

  startProcessing: () => Promise<void>;
  submitClarifications: (responses: Record<string, string>) => Promise<void>;
  confirmSave: () => Promise<void>;

  // ============================================================================
  // Internal Actions
  // ============================================================================

  _updateSessionStatus: (status: AgentSessionStatus) => void;
  _addMessage: (type: string, content: string, metadata?: Record<string, unknown>) => void;

  // ============================================================================
  // Reset
  // ============================================================================

  resetDatabase: () => { success: boolean; deletedCounts: Record<string, number> };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useAgentStore = create<AgentState>((set, get) => ({
  // Initial State
  currentSessionId: null,
  sessions: [],
  messages: [],
  isProcessing: false,
  uploadedFiles: [],
  extractedEntities: null,
  extractedCounts: null,
  pendingClarifications: [],
  clarificationResponses: {},
  validationErrors: [],
  report: null,

  // ============================================================================
  // Getters
  // ============================================================================

  getCurrentSession: () => {
    const { currentSessionId, sessions } = get();
    return sessions.find((s) => s.id === currentSessionId) ?? null;
  },

  getSessionMessages: () => {
    const { currentSessionId, messages } = get();
    return messages
      .filter((m) => m.sessionId === currentSessionId)
      .sort((a, b) => a.createdAt - b.createdAt);
  },

  getSessionStatus: () => {
    const session = get().getCurrentSession();
    return session?.status ?? null;
  },

  hasPendingClarifications: () => {
    return get().pendingClarifications.length > 0;
  },

  hasValidationErrors: () => {
    return get().validationErrors.some((e) => e.severity === 'error');
  },

  // ============================================================================
  // Session Actions
  // ============================================================================

  createSession: (config) => {
    const id = generateId('session');
    const now = Date.now();

    const newSession: CpAgentSession = {
      id,
      status: 'initializing',
      startedAt: now,
      config: { ...defaultAgentConfig, ...config },
      uploadedFileIds: [],
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({
      currentSessionId: id,
      sessions: [...state.sessions, newSession],
      messages: [],
      uploadedFiles: [],
      extractedEntities: null,
      extractedCounts: null,
      pendingClarifications: [],
      clarificationResponses: {},
      validationErrors: [],
      report: null,
    }));

    // Add initial message
    get()._addMessage('progress', 'Session created. Please upload your curriculum files to begin.');

    return id;
  },

  clearSession: () => {
    set({
      currentSessionId: null,
      messages: [],
      uploadedFiles: [],
      extractedEntities: null,
      extractedCounts: null,
      pendingClarifications: [],
      clarificationResponses: {},
      validationErrors: [],
      report: null,
      isProcessing: false,
    });
  },

  cancelSession: () => {
    const { currentSessionId } = get();
    if (!currentSessionId) return;

    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === currentSessionId
          ? { ...s, status: 'error' as AgentSessionStatus, error: 'Session cancelled by user', updatedAt: Date.now() }
          : s
      ),
      isProcessing: false,
    }));

    get()._addMessage('error', 'Session cancelled by user.');
    get().clearSession();
  },

  // ============================================================================
  // File Actions
  // ============================================================================

  uploadFiles: async (files) => {
    const { currentSessionId } = get();
    if (!currentSessionId) return;

    // Simulate upload delay
    set({ isProcessing: true });

    const uploadedFiles = files.map((file) => ({
      id: generateId('file'),
      name: file.name,
      size: file.size,
    }));

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    set((state) => ({
      uploadedFiles: [...state.uploadedFiles, ...uploadedFiles],
      sessions: state.sessions.map((s) =>
        s.id === currentSessionId
          ? {
              ...s,
              uploadedFileIds: [...s.uploadedFileIds, ...uploadedFiles.map((f) => f.id)],
              updatedAt: Date.now(),
            }
          : s
      ),
      isProcessing: false,
    }));

    get()._addMessage(
      'progress',
      `Uploaded ${files.length} file(s): ${files.map((f) => f.name).join(', ')}`
    );
  },

  clearUploadedFiles: () => {
    set({ uploadedFiles: [] });
  },

  // ============================================================================
  // Processing Actions
  // ============================================================================

  startProcessing: async () => {
    const { currentSessionId, uploadedFiles } = get();
    if (!currentSessionId || uploadedFiles.length === 0) return;

    set({ isProcessing: true });

    const simulator = new AgentSimulator({
      onStatusChange: (status) => get()._updateSessionStatus(status as AgentSessionStatus),
      onMessage: (type, content, metadata) => get()._addMessage(type, content, metadata),
      onProgress: (step, total, description) => {
        get()._addMessage('progress', `Step ${step}/${total}: ${description}`);
      },
    });

    try {
      // Run extraction
      const fileNames = uploadedFiles.map((f) => f.name);
      const extracted = await simulator.processFiles(fileNames);

      const counts = {
        teachers: extracted.teachers.length,
        subjects: extracted.subjects.length,
        bands: extracted.bands.length,
        sets: extracted.sets.length,
        optionBlocks: extracted.optionBlocks.length,
      };

      set({
        extractedEntities: extracted,
        extractedCounts: counts,
      });

      // Generate clarifications
      const clarifications = simulator.generateClarifications();
      const validationErrors = simulator.generateValidationErrors();

      set({
        pendingClarifications: clarifications,
        validationErrors,
      });

      // Update session status
      if (clarifications.length > 0) {
        get()._updateSessionStatus('clarifying');
        get()._addMessage(
          'question',
          `Found ${clarifications.length} item(s) that need clarification before saving.`
        );
      } else {
        get()._updateSessionStatus('waiting_for_user');
        get()._addMessage('progress', 'Extraction complete. Ready to save data.');
      }
    } catch (error) {
      get()._updateSessionStatus('error');
      get()._addMessage('error', `Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      set({ isProcessing: false });
    }
  },

  submitClarifications: async (responses) => {
    const { pendingClarifications } = get();
    if (pendingClarifications.length === 0) return;

    set({ isProcessing: true, clarificationResponses: responses });

    get()._addMessage('answer', `Received ${Object.keys(responses).length} clarification response(s).`);

    // Simulate processing
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Clear clarifications and move to waiting
    set({
      pendingClarifications: [],
      isProcessing: false,
    });

    get()._updateSessionStatus('waiting_for_user');
    get()._addMessage('progress', 'Clarifications processed. Ready to save data.');
  },

  confirmSave: async () => {
    const { extractedEntities, extractedCounts } = get();
    if (!extractedEntities) return;

    set({ isProcessing: true });
    get()._updateSessionStatus('writing');
    get()._addMessage('progress', 'Saving extracted data to the database...');

    // Simulate save delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Actually save to the curriculum planning store
    const cpStore = useCurriculumPlanningStore.getState();
    const savedCounts: Record<string, number> = {};

    // Save teachers
    if (extractedEntities.teachers.length > 0) {
      extractedEntities.teachers.forEach((teacher) => {
        if (teacher.firstName && teacher.lastName && teacher.email) {
          cpStore.addTeacher({
            firstName: teacher.firstName,
            lastName: teacher.lastName,
            initials: teacher.initials || `${teacher.firstName[0]}${teacher.lastName[0]}`,
            email: teacher.email,
            employeeCode: teacher.employeeCode || generateId('emp'),
            fte: teacher.fte || 1.0,
            maxPeriodsPerWeek: teacher.maxPeriodsPerWeek || 22,
            maxPeriodsPerCycle: teacher.maxPeriodsPerCycle || 22,
          });
        }
      });
      savedCounts.teachers = extractedEntities.teachers.length;
    }

    // Save subjects
    if (extractedEntities.subjects.length > 0) {
      extractedEntities.subjects.forEach((subject) => {
        if (subject.name && subject.code && subject.categoryId) {
          cpStore.addSubject({
            name: subject.name,
            code: subject.code,
            categoryId: subject.categoryId,
            requiresSpecialistRoom: subject.requiresSpecialistRoom || false,
            maxClassSize: subject.maxClassSize,
          });
        }
      });
      savedCounts.subjects = extractedEntities.subjects.length;
    }

    // Save bands
    if (extractedEntities.bands.length > 0) {
      extractedEntities.bands.forEach((band) => {
        if (band.name && band.code && band.yearGroupId && band.academicYearId) {
          cpStore.addBand({
            name: band.name,
            code: band.code,
            yearGroupId: band.yearGroupId,
            academicYearId: band.academicYearId,
            displayOrder: band.displayOrder || 0,
          });
        }
      });
      savedCounts.bands = extractedEntities.bands.length;
    }

    // Save sets
    if (extractedEntities.sets.length > 0) {
      extractedEntities.sets.forEach((set) => {
        if (set.name && set.code && set.yearGroupId && set.academicYearId) {
          cpStore.addSet({
            name: set.name,
            code: set.code,
            yearGroupId: set.yearGroupId,
            academicYearId: set.academicYearId,
            bandId: set.bandId,
            pupilCount: set.pupilCount || 0,
            capacity: set.capacity || 30,
            displayOrder: set.displayOrder || 0,
          });
        }
      });
      savedCounts.sets = extractedEntities.sets.length;
    }

    // Save option blocks
    if (extractedEntities.optionBlocks.length > 0) {
      extractedEntities.optionBlocks.forEach((block) => {
        if (block.name && block.code && block.yearGroupId && block.academicYearId) {
          cpStore.addOptionBlock({
            name: block.name,
            code: block.code,
            yearGroupId: block.yearGroupId,
            academicYearId: block.academicYearId,
            periodsPerCycle: block.periodsPerCycle || 4,
            minSubjectsToChoose: block.minSubjectsToChoose || 1,
            maxSubjectsToChoose: block.maxSubjectsToChoose || 1,
            displayOrder: block.displayOrder || 0,
          });
        }
      });
      savedCounts.optionBlocks = extractedEntities.optionBlocks.length;
    }

    get()._addMessage('progress', 'Data saved successfully!');

    // Generate report
    const simulator = new AgentSimulator({
      onStatusChange: () => {},
      onMessage: () => {},
      onProgress: () => {},
    });

    const report = simulator.generateReport(extractedCounts || {}, savedCounts);

    set({
      report,
      isProcessing: false,
    });

    get()._updateSessionStatus('complete');
    get()._addMessage('report', report);

    // Update session
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === state.currentSessionId
          ? {
              ...s,
              status: 'complete' as AgentSessionStatus,
              completedAt: Date.now(),
              report,
              savedEntityIds: JSON.stringify(savedCounts),
              updatedAt: Date.now(),
            }
          : s
      ),
    }));
  },

  // ============================================================================
  // Internal Actions
  // ============================================================================

  _updateSessionStatus: (status) => {
    const { currentSessionId } = get();
    if (!currentSessionId) return;

    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === currentSessionId ? { ...s, status, updatedAt: Date.now() } : s
      ),
    }));
  },

  _addMessage: (type, content, metadata) => {
    const { currentSessionId } = get();
    if (!currentSessionId) return;

    const message: CpAgentMessage = {
      id: generateId('msg'),
      sessionId: currentSessionId,
      role: 'agent',
      type: type as CpAgentMessage['type'],
      content,
      metadata: metadata ? JSON.stringify(metadata) : undefined,
      createdAt: Date.now(),
    };

    set((state) => ({
      messages: [...state.messages, message],
    }));
  },

  // ============================================================================
  // Reset
  // ============================================================================

  resetDatabase: () => {
    const cpStore = useCurriculumPlanningStore.getState();

    // Get counts before reset
    const deletedCounts = {
      academicYears: cpStore.academicYears.length,
      terms: cpStore.terms.length,
      subjects: cpStore.subjects.length,
      teachers: cpStore.teachers.length,
      bands: cpStore.bands.length,
      sets: cpStore.sets.length,
      classes: cpStore.classes.length,
      optionBlocks: cpStore.optionBlocks.length,
      curriculumPlans: cpStore.curriculumPlans.length,
    };

    // Reset curriculum planning store
    cpStore.resetAllData();

    // Clear agent session
    get().clearSession();

    return { success: true, deletedCounts };
  },
}));

export default useAgentStore;
